import { useState, useMemo } from 'react'
import {
    Box,
    Text,
    HStack,
    Avatar,
    VStack,
    IconButton,
    useDisclosure
} from '@chakra-ui/react'
import { FiTrash2 } from 'react-icons/fi'
import { AlternativeContactUserCardData } from './type'
import { useDeleteAlternativeContact } from '../hooks/useDeleteAlternativeContact'
import DeleteConfirmationDialog from './DeleteConfirmationDialog'
import { useAlertService } from '@/shared/lib/useAlertService'
import { useAuth } from '@/shared/context/AuthContext'

function AlternativeContactUserCard({
    alternativeContact
}: AlternativeContactUserCardData) {
    const alertService = useAlertService()
    const { user } = useAuth()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const { handleDeleteContact, loading } = useDeleteAlternativeContact()
    const [selectedChildId, setSelectedChildId] = useState<string | null>(null)

    const contactName = useMemo(() => {
        if (!selectedChildId) {
            return `${alternativeContact.firstName} ${alternativeContact.lastName}`
        }
        const selectedChild = alternativeContact.children.find(
            (c) => c._id === selectedChildId
        )

        return `${selectedChild?.firstName || ''} from contact`
    }, [selectedChildId, alternativeContact])

    const handleContactDeletion = async () => {
        try {
            await handleDeleteContact(
                alternativeContact._id,
                user?.parent?._id || '',
                selectedChildId || undefined
            )
            onClose()
            setSelectedChildId(null)
            alertService.success(
                selectedChildId ? 'Child removed from contact' : 'Contact deleted'
            )
        } catch {
            alertService.error(
                selectedChildId ? 'Error removing child' : 'Error deleting contact'
            )
        }
    }

    const handleOpenDialog = (childId?: string) => {
        setSelectedChildId(childId || null)
        onOpen()
    }

    return (
        <>
            <Box
                bg={'gray.50'}
                color={'brand'}
                w="full"
                maxW="sm"
                height={'fit-content'}
                p={4}
                borderWidth={1}
                borderRadius="md"
                boxShadow="md"
            >
                <HStack spacing={4} align="center">
                    <Avatar
                        size="xl"
                        name={alternativeContact.firstName}
                        src={alternativeContact.picture || ''}
                    />
                    <VStack spacing={1} textAlign="center">
                        <Text fontSize="xl" fontWeight="bold">
                            {alternativeContact.firstName} {alternativeContact.lastName}
                        </Text>
                    </VStack>
                    <IconButton
                        aria-label="Delete contact"
                        icon={<FiTrash2 />}
                        colorScheme="red"
                        variant={'outline'}
                        size="md"
                        onClick={() => handleOpenDialog()}
                    />
                </HStack>
                <HStack spacing={4} align="baseline">
                    {alternativeContact.children.length > 0 && (
                        <Box mt={4}>
                            <Text py={2}>Assigned Children:</Text>
                            <VStack align="start" spacing={1}>
                                {alternativeContact.children.map((child) => (
                                    <HStack key={child._id} py={1} spacing={2}>
                                        <Avatar
                                            size="md"
                                            name={child.firstName}
                                            src={child.picture || ''}
                                        />
                                        <Text>
                                            {child.firstName} {child.lastName}
                                        </Text>
                                        <IconButton
                                            ml={2}
                                            aria-label="Delete contact"
                                            icon={<FiTrash2 />}
                                            colorScheme="red"
                                            variant={'outline'}
                                            size="sm"
                                            onClick={() => handleOpenDialog(child._id)}
                                        />
                                    </HStack>
                                ))}
                            </VStack>
                        </Box>
                    )}
                </HStack>
            </Box>
            <DeleteConfirmationDialog
                isOpen={isOpen}
                onClose={onClose}
                onDelete={handleContactDeletion}
                isLoading={loading}
                contactName={contactName}
            />
        </>
    )
}

export default AlternativeContactUserCard
